import { useEffect, useState } from 'react';
import type { EmergencyDrillPayload, EmergencyDrillResult, EmergencyPlanSummary } from './types';
import { DRILL_RESULT_LABEL } from './emergencyUtils';

const RESULTS = Object.keys(DRILL_RESULT_LABEL) as EmergencyDrillResult[];

interface DrillFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: EmergencyDrillPayload) => Promise<void>;
  plans: EmergencyPlanSummary[];
  initial?: Partial<EmergencyDrillPayload>;
  title: string;
}

function toDateInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultPayload(plans: EmergencyPlanSummary[]): EmergencyDrillPayload {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  return {
    planId: plans[0]?.id ?? '',
    date,
    participants: '',
    result: 'satisfactorio',
    findingsNote: '',
  };
}

export function DrillFormModal({
  open,
  onClose,
  onSubmit,
  plans,
  initial,
  title,
}: DrillFormModalProps) {
  const [form, setForm] = useState<EmergencyDrillPayload>(defaultPayload(plans));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = defaultPayload(plans);
    setForm({
      ...base,
      ...initial,
      date: toDateInput(initial?.date) || base.date,
      planId: initial?.planId ?? base.planId,
    });
    setError(null);
  }, [open, initial, plans]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        ...form,
        findingsNote: form.findingsNote?.trim() ? form.findingsNote.trim() : null,
      });
      onClose();
    } catch {
      setError('No se pudo guardar el simulacro.');
    } finally {
      setSaving(false);
    }
  };

  const showFindings = form.result !== 'satisfactorio';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-card p-6 shadow-lg border border-border max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="drill-plan">
              Plan de emergencia
            </label>
            <select
              id="drill-plan"
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              value={form.planId}
              onChange={(e) => setForm((prev) => ({ ...prev, planId: e.target.value }))}
              required
            >
              {plans.length === 0 && <option value="">Sin planes activos</option>}
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="drill-date">
                Fecha del simulacro
              </label>
              <input
                id="drill-date"
                type="date"
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                value={form.date}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="drill-result">
                Resultado
              </label>
              <select
                id="drill-result"
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                value={form.result}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, result: e.target.value as EmergencyDrillResult }))
                }
              >
                {RESULTS.map((value) => (
                  <option key={value} value={value}>
                    {DRILL_RESULT_LABEL[value]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="drill-participants">
              Participantes
            </label>
            <textarea
              id="drill-participants"
              className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[72px]"
              value={form.participants}
              onChange={(e) => setForm((prev) => ({ ...prev, participants: e.target.value }))}
              required
              maxLength={10000}
              placeholder="Áreas, brigadistas, cantidad de participantes…"
            />
          </div>

          {showFindings && (
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="drill-findings">
                Hallazgos / observaciones
              </label>
              <textarea
                id="drill-findings"
                className="w-full rounded-md border border-input px-3 py-2 text-sm min-h-[96px]"
                value={form.findingsNote ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, findingsNote: e.target.value }))}
                maxLength={10000}
                placeholder="Desvíos, tiempos de evacuación, acciones de mejora…"
              />
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || plans.length === 0}
            >
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
